import React, { createContext, MutableRefObject, useContext, useEffect, useState } from 'react';
import { Logger } from '@westwing/appshell_types/loggerTypes';
import { StyleVariantType } from '@westwing/appshell_types';
import { CartAPIFullData, CartMetaData } from '../types/oneCartAPI';
import RequestCartInfo from '../helpers/RequestCartInfo';
import { clubLinks } from '../clubLinks';
import { ClubLink } from '../types/clubLinksTypes';
import { getCartProducts } from '../helpers/cartV2.helpers';
import { debounce } from '../../helpers/debounce';
import { defaultCartMetadataValue, defaultCartValue } from '../../helpers/defaultAPIvalue';

interface OneCartContextValue {
    data: CartAPIFullData['data'] | undefined;
    metadata: CartMetaData['data'] | undefined;
    isFetching: boolean;
    fetchCartData: () => void;
    fetchCartMetaData: () => void;
    isOneCartEnabled: boolean;
    country: string;
    clearOneCartContext: () => void;
}

const OneCartContext = createContext<OneCartContextValue | undefined>(undefined);

export const useOneCartContext = (): OneCartContextValue => {
    const context = useContext(OneCartContext);
    if (!context) {
        throw new Error('useOneCartContext must be used within a OneCartContextProvider');
    }
    return context;
};

interface OneCartContextProviderProps {
    isEnabled: boolean;
    country: string;
    logger: Logger;
    fetchCartDataOnce?: MutableRefObject<boolean>;
    children: React.ReactNode;
    styleVariant: StyleVariantType;
}

export const OneCartContextProvider: React.FC<OneCartContextProviderProps> = ({
    isEnabled,
    country,
    logger,
    fetchCartDataOnce,
    styleVariant,
    children,
}) => {
    const [data, setData] = useState<OneCartContextValue['data']>(undefined);
    const [metadata, setMetaData] = useState<OneCartContextValue['metadata']>(undefined);
    const [isFetching, setIsFetching] = useState(false);
    const isShopCartPage = styleVariant === 'bella';

    const fetchCartData = async (): Promise<OneCartContextValue['data']> => {
        const requestPath = clubLinks[ClubLink.ONE_CART_REQUEST].path.replace(':country', country?.toLowerCase());
        const url = `${requestPath}?country=${country?.toLowerCase()}`;

        const response = await RequestCartInfo<CartAPIFullData>(url);
        if (response.success) {
            response.data.cart.itemList = getCartProducts(response.data, country);
            return response.data;
        }

        logger.error('[OneCartContext] The cart API call was unsuccessful. Returning default value', {
            response: JSON.stringify(response),
        });

        return defaultCartValue;
    };

    const fetchCartMetaData = async (): Promise<OneCartContextValue['metadata']> => {
        const requestPath = clubLinks[ClubLink.ONE_CART_METADATA].path.replace(':country', country?.toLowerCase());
        const url = `${requestPath}?country=${country?.toLowerCase()}`;

        const response = await RequestCartInfo<CartMetaData>(url);
        if (response.success) {
            return response.data;
        }

        logger.error('[OneCartContext] The cart-metadata API call was unsuccessful. Returning default value', {
            response: JSON.stringify(response),
        });

        return defaultCartMetadataValue;
    };

    const { run: fetchCartDataAndUpdateState } = debounce(() => {
        // INFO: We don't need to fetch full cart data from OWW header for shop cart page. The call already handled in MF-Cart itself.
        if (isShopCartPage) {
            return;
        }

        setIsFetching(true);
        fetchCartData()
            .then(response => {
                setData(response);
            })
            .catch(error => {
                logger.error('[OneCartContext] Failed when retrieving cart data:', {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                });
            })
            .finally(() => {
                setIsFetching(false);
            });
    });

    const { run: fetchMetaDataAndUpdateState } = debounce(() => {
        setIsFetching(true);
        fetchCartMetaData()
            .then(response => {
                setMetaData(response);
            })
            .catch(error => {
                logger.error('[OneCartContext] Failed when retrieving cart metaData:', {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                });
            })
            .finally(() => {
                setIsFetching(false);
            });
    });

    const contextValue: OneCartContextValue = {
        data,
        metadata,
        country,
        isFetching,
        fetchCartData: fetchCartDataAndUpdateState,
        fetchCartMetaData: fetchMetaDataAndUpdateState,
        isOneCartEnabled: isEnabled,
        clearOneCartContext: () => setData(undefined),
    };

    useEffect(() => {
        if (!isEnabled || fetchCartDataOnce?.current) {
            return;
        }

        if (fetchCartDataOnce !== undefined) {
            // eslint-disable-next-line no-param-reassign
            fetchCartDataOnce.current = true;
        }

        if (isShopCartPage) {
            fetchMetaDataAndUpdateState();
        } else {
            fetchCartDataAndUpdateState();
        }
    }, [fetchCartDataOnce, fetchCartDataAndUpdateState, fetchMetaDataAndUpdateState, isEnabled, isShopCartPage]);

    return <OneCartContext.Provider value={contextValue}>{children}</OneCartContext.Provider>;
};
